import type { AdminAction, ParticipantView } from '../event/state'
import type { EventState } from '../event/types'

/**
 * The event API client. Same origin, so the session cookie travels with every
 * request and there is no base URL to configure.
 */

export type Role = 'participant' | 'admin'
export type SessionInfo = { role: Role; email: string }

export class ApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

/** Raised when the server says the session is gone, so every view can sign out at once. */
export const SIGNED_OUT_EVENT = 'fiti:signed-out'

/** Sign-in endpoints answer 401 as part of their normal work; anything else means the session ended. */
function reportSessionEnded(path: string, status: number) {
  if (status === 401 && path !== '/api/session' && path !== '/api/login' && typeof window !== 'undefined') {
    window.dispatchEvent(new Event(SIGNED_OUT_EVENT))
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: { accept: 'application/json', ...(init?.body ? { 'content-type': 'application/json' } : {}) },
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    reportSessionEnded(path, response.status)
    throw new ApiError(body?.error ?? `Request failed (${response.status}).`, response.status)
  }
  return body as T
}

export const login = (role: Role, email: string, password: string) =>
  request<SessionInfo>('/api/login', { method: 'POST', body: JSON.stringify({ role, email, password }) })

export const logout = () => request<{ ok: true }>('/api/logout', { method: 'POST' })

export async function getSession(): Promise<SessionInfo | null> {
  try {
    return await request<SessionInfo>('/api/session')
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null
    throw error
  }
}

export const getParticipantState = () => request<ParticipantView>('/api/state')
export const getAdminState = () => request<EventState>('/api/state')

export type AdminResult = { state: EventState; error: string | null }

/**
 * Sends one action against the revision on screen. A refusal or a conflict
 * still returns the server's current document, so the screen always shows
 * what is actually saved.
 */
export async function adminAction(revision: number, action: AdminAction): Promise<AdminResult> {
  const response = await fetch('/api/admin', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    body: JSON.stringify({ revision, action }),
  })
  const body = await response.json().catch(() => ({}))
  if (response.ok) return { state: body as EventState, error: null }
  reportSessionEnded('/api/admin', response.status)
  if (body?.state) return { state: body.state as EventState, error: body.error ?? 'The change was not saved.' }
  throw new ApiError(body?.error ?? `Request failed (${response.status}).`, response.status)
}
